import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Button, Grid, Stack, Typography } from '@mui/material';

import { UserApi } from '@/api';
import { IcWarning } from '@/assets/images';
import { AppModal, FormRoot, RenderFormField } from '@/components';
import {
  DeviceNcicOriForm,
  DeviceNcicOriFormFields,
  DeviceOriFormDefaultValues,
} from '@/pages/Employees/data';
import { colors } from '@/theme/variables';

interface DeviceNcicOriModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (value: DeviceNcicOriForm) => void;
  ori?: string;
  mne?: string;
}

export const DeviceNcicOriModal = ({
  open,
  onClose,
  onSubmit,
  ori,
  mne,
}: DeviceNcicOriModalProps) => {
  const methods = useForm<DeviceNcicOriForm>({
    mode: 'all',
    defaultValues: DeviceOriFormDefaultValues,
  });
  const { handleSubmit, watch } = methods;
  const [showPopup, setShowPopup] = useState(false);
  const [exist, setExist] = useState<'ori' | 'mnemonic' | ''>('');
  const oriNumber = watch('oriNumber');
  const mnemonic = watch('mnemonic');

  useEffect(() => {
    if (ori) {
      methods.setValue('oriNumber', ori, { shouldDirty: true });
    }
    if (mne) {
      methods.setValue('mnemonic', mne, { shouldDirty: true });
    }
  }, [ori, mne]);

  useEffect(() => {
    const fetchData = async () => {
      if (!oriNumber && !mnemonic) {
        return;
      }
      const oriExists = await UserApi.checkDeviceNcic(oriNumber, mnemonic);
      setExist(oriExists.data.mnemonic ? 'mnemonic' : 'ori');
      setShowPopup(!!oriExists.data.mnemonic || !!oriExists.data.oriNumber);
    };

    fetchData();
  }, [oriNumber, mnemonic]);

  return (
    <AppModal
      open={open}
      title="Assign Device ORI"
      onClose={onClose}
      sx={{
        zIndex: 10000,
        '.MuiPaper-root': {
          overflow: 'visible',
        },
      }}
    >
      <FormProvider {...methods}>
        <Stack sx={{ width: '600px' }}>
          <Stack>
            Please enter the Device ORI to associate with this device for NCIC
            access. This ORI will be required for authentication and audit
            tracking.
          </Stack>

          <FormRoot>
            <Grid columnSpacing={2} flex={1} container>
              {DeviceNcicOriFormFields.map(({ grid, ...rest }) => {
                return (
                  <Grid key={rest.name} {...grid} item>
                    <RenderFormField {...rest} />
                  </Grid>
                );
              })}
            </Grid>
          </FormRoot>

          {showPopup && (
            <Stack
              borderRadius={2}
              border={`1px solid ${colors.warning.main}`}
              bgcolor="#FFF5CC"
              flexDirection="row"
              padding="8px"
            >
              <IcWarning width={24} height={24} color={colors.warning.main} />
              <Stack sx={{ ml: 1 }}>
                <Typography component="h3" variant="h6">
                  Warning
                </Typography>
                <Typography component="p" color={colors.grey['70']}>
                  {`This ${
                    exist === 'mnemonic' ? 'mnemonic' : 'ORI'
                  } is already in use on another device. You may still
                  proceed, but this may violate uniqueness expectations.`}
                </Typography>
              </Stack>
            </Stack>
          )}

          <Stack
            flexDirection="row"
            justifyContent="flex-end"
            width={350}
            height={70}
            ml="auto"
          >
            <Button
              variant="outlined"
              color="inherit"
              size="large"
              sx={{ mt: 2.5, ml: 2, flex: 1 }}
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              color="error"
              size="large"
              sx={{ mt: 2.5, ml: 2, flex: 1 }}
              onClick={handleSubmit(onSubmit)}
              fullWidth
            >
              Assign ORI & Approve
            </Button>
          </Stack>
        </Stack>
      </FormProvider>
    </AppModal>
  );
};
